import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Modal, Table, Form, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';

import { FlightTicketApiService, SettingsApiService } from 'Services/ApiServices';
import { setLoader } from 'Store/Actions/Loading/LoadingAction';
import { toastr } from 'Helpers/ToastHelper';
import { DateTimeFormater } from 'Helpers/DateTimeHelper';
import { useModal, useApiData, useDarkMode } from 'Hooks';
import { DS_F2W_Business_Travel_Team } from 'Constants/F2wRolesConstants';
import { DATETIME_FORMAT_WEB } from 'Constants/DateFormatConstants';
import { FlightTicketPropType } from 'PropTypes';

const CommentsCard = (props) => {
  const { flyToWorkTicketInfo, setRefreshComponent, isLockRequired, resetLockTimers } = props;
  const dispatch = useDispatch();
  const { darkMode } = useDarkMode();

  const { data: settings } = useApiData(() => SettingsApiService.getAll());
  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles || [];

  const { isOpen: showAddCommentModal, open: openModal, close: closeModal } = useModal();
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');

  const templatesOptions = (settings?.templates || [])
    .filter((template) => template.dropdownName === 'template_messages')
    .flatMap(
      (template) =>
        template.options?.map((option) => ({
          value: option.description,
          label: option.title,
          category: option.category,
        })) || []
    );

  const handleInsertComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      setCommentError('Comment is required');
      return;
    }

    // Refresh/confirm the editing lock before mutating, mirroring the ticket
    // submit/delete flows. Without this a stale or expired lock makes the API
    // reject the insert with "A valid ticket lock is required before editing".
    if (isLockRequired && resetLockTimers && !(await resetLockTimers())) return;

    dispatch(setLoader(true));
    FlightTicketApiService.addComment(flyToWorkTicketInfo.id, comment)
      .then((response) => {
        const data = response?.data || response;
        if (data?.id) {
          toastr.success('Success', 'Comment added successfully');
          closeModal();
          setComment('');
          setCommentError('');
          setRefreshComponent((prev) => prev + 1);
        } else {
          toastr.error('Error', response?.message || 'Failed to add comment');
        }
      })
      .catch((error) => {
        const apiMessage = error?.response?.data?.message;
        toastr.error('Error', apiMessage || 'Failed to add comment');
      })
      .finally(() => {
        dispatch(setLoader(false));
      });
  };

  const handleRemoveComment = async (commentId) => {
    if (isLockRequired && resetLockTimers && !(await resetLockTimers())) return;

    toastr.confirm('Are you sure you want to remove this comment?', {
      onOk: () => {
        dispatch(setLoader(true));
        FlightTicketApiService.deleteComment(flyToWorkTicketInfo.id, commentId)
          .then((response) => {
            if (response) {
              toastr.success('Success', 'Comment removed successfully');
              setRefreshComponent((prev) => prev + 1);
            }
          })
          .catch(() => {
            toastr.error('Error', 'Failed to remove comment');
          })
          .finally(() => {
            dispatch(setLoader(false));
          });
      },
    });
  };

  const handleTemplateSelect = (selectedOption) => {
    setComment(selectedOption?.value || '');
    setCommentError('');
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#212529' : base.backgroundColor,
      borderColor: darkMode ? '#495057' : base.borderColor,
      color: darkMode ? '#fff' : base.color,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#212529' : base.backgroundColor,
    }),
    option: (base, state) => {
      let bgColor;
      if (state.isFocused) {
        bgColor = darkMode ? '#495057' : '#e9ecef';
      } else {
        bgColor = darkMode ? '#212529' : base.backgroundColor;
      }

      return {
        ...base,
        backgroundColor: bgColor,
        color: darkMode ? '#fff' : base.color,
      };
    },
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#fff' : base.color,
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#fff' : base.color,
    }),
  };

  const handleCloseModal = () => {
    closeModal();
    setComment('');
    setCommentError('');
  };

  return (
    <>
      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <i className="fa fa-comments me-2" />
            Comments
            <Badge bg="secondary" className="ms-2">
              {flyToWorkTicketInfo.comments?.length || 0}
            </Badge>
          </div>
          <Button variant="primary" size="sm" onClick={openModal}>
            <i className="fa fa-plus me-1" />
            Add Comment
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {flyToWorkTicketInfo.comments?.length > 0 ? (
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Created by</th>
                  <th style={{ width: '10%' }}>Group</th>
                  <th style={{ width: '45%' }}>Comment</th>
                  <th style={{ width: '20%' }}>Timestamp</th>
                  {userRoles.includes(DS_F2W_Business_Travel_Team) && <th style={{ width: '10%' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {flyToWorkTicketInfo.comments.map((comment) => (
                  <tr key={comment.id || `${comment.createdBy}-${comment.created}`}>
                    <td className="align-middle">{comment.createdBy}</td>
                    <td className="align-middle">
                      <Badge bg="info">{comment.group}</Badge>
                    </td>
                    <td className="align-middle" style={{ whiteSpace: 'pre-wrap' }}>
                      {comment.text}
                    </td>
                    <td className="align-middle">{DateTimeFormater(comment.created, null, DATETIME_FORMAT_WEB)}</td>
                    {userRoles.includes(DS_F2W_Business_Travel_Team) && (
                      <td className="align-middle">
                        <Button variant="danger" size="sm" onClick={() => handleRemoveComment(comment.id)}>
                          <i className="fa fa-trash" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fa fa-comments fa-3x mb-3 d-block opacity-50" />
              <p className="text-muted">No comments yet. Be the first to add one!</p>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal size="lg" show={showAddCommentModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-comment me-2" />
            Add New Comment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleInsertComment}>
            {userRoles.includes(DS_F2W_Business_Travel_Team) && templatesOptions.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="fa fa-file-text-o me-2" />
                  Use Template (Optional)
                </Form.Label>
                <Select
                  options={templatesOptions}
                  isSearchable
                  isClearable
                  placeholder="Select a template to auto-fill..."
                  onChange={handleTemplateSelect}
                  menuPlacement="auto"
                  styles={selectStyles}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>
                Comment <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  setCommentError('');
                }}
                isInvalid={!!commentError}
                placeholder="Enter your comment here..."
                // autoFocus
              />
              <Form.Control.Feedback type="invalid">{commentError}</Form.Control.Feedback>
              <Form.Text className="text-muted">{comment.length} characters</Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            <i className="fa fa-times me-1" />
            Cancel
          </Button>
          <Button variant="primary" onClick={handleInsertComment} disabled={!comment.trim()}>
            <i className="fa fa-check me-1" />
            Add Comment
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

CommentsCard.propTypes = {
  flyToWorkTicketInfo: FlightTicketPropType.isRequired,
  setRefreshComponent: PropTypes.func.isRequired,
  refreshComponent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLockRequired: PropTypes.bool,
  resetLockTimers: PropTypes.func,
};

export default CommentsCard;
